<template>
  <v-container fluid class="agenda agenda--partner">
    <page-skeleton v-if="loading" variant="calendar" class="mt-4" />

    <template v-else>
      <header class="agenda__head">
        <div>
          <div class="agenda__eyebrow">{{ $t("agenda.teacher") }}</div>
          <h1 class="agenda__title">{{ $t("agenda.title") }}</h1>
          <p class="agenda__sub">
            {{ $t("agenda.partnerSub") }}
          </p>
        </div>

        <button type="button" class="agenda__add" @click="openIncluirAula">
          <v-icon size="20" color="#fff">mdi-plus</v-icon>
          {{ $t("agenda.includeLesson") }}
        </button>
      </header>

      <div class="agenda__panel">
        <AppCalendar
          :items="events"
          :availability="availability"
          @adiar="adiar"
          @reload="loadData"
          @general-class-attendance="openGeneralClassAttendance"
        />
      </div>
    </template>

    <IncluirAulaDialog
      ref="incluirAula"
      :groups="groups"
      :availability="availability"
      @created="loadData"
    />

    <GeneralClassAttendanceDialog ref="generalClassAttendance" />
  </v-container>
</template>

<script>
import AppCalendar from "@/components/calendar/calendar";
import IncluirAulaDialog from "@/components/calendar/incluirAulaDialog";
import GeneralClassAttendanceDialog from "@/components/calendar/generalClassAttendanceDialog";
import partnerApi from "@/shared/pitchPartnerApi";

export default {
  name: "PartnerCalendar",
  components: {
    AppCalendar,
    IncluirAulaDialog,
    GeneralClassAttendanceDialog,
  },
  created() {
    this.loadData();
    this.loadComplementaryClassData();
  },
  data() {
    return {
      loading: true,
      events: [],
      groups: [],
      availability: [],
    };
  },
  methods: {
    loadData() {
      this.loading = true;
      partnerApi.calendar().then((res) => {
        this.events = res.data.events;
        this.loading = false;
      });
    },
    loadComplementaryClassData() {
      partnerApi.getStudyGroups().then((res) => {
        this.groups = res.data || [];
      });
      partnerApi.showProfile().then((res) => {
        this.availability = res.data.aviability || [];
      });
    },
    openIncluirAula() {
      this.$refs.incluirAula.open();
    },
    openGeneralClassAttendance(sessionId) {
      this.$refs.generalClassAttendance.open(sessionId);
    },
    adiar(item) {
      let request;
      const { action } = item;

      switch (action) {
        case "cancel": {
          if (window.confirm(this.$t("agenda.confirmCancelPartner"))) {
            const { motivo } = item;
            request = partnerApi.cancelarAula(item.item.id, {
              motivo,
              shiftSubjects: !!item.item.shiftSubjects,
            });
          }
          break;
        }
        case "next": {
          if (window.confirm(this.$t("agenda.confirmSubject"))) {
            const { data } = item.item;
            request = partnerApi.adiarParaProxima(item.item.id, data);
          }
          break;
        }
        case "other": {
          request = partnerApi.postergate(item.item.id, item.item.data);
          break;
        }
        // A confirmação já foi feita no diálogo, com o de/para na tela.
        case "permanent": {
          request = partnerApi.postergatePermanent(
            item.item.id,
            item.item.data
          );
          break;
        }
        default:
          return;
      }

      if (!request) return;

      request
        .then((res) => {
          this.$root.$emit("display-alert", {
            text: this.successMessage(action, res?.data?.status),
            type: 0,
          });
          this.loadData();
        })
        .catch((error) => {
          this.$root.$emit("display-alert", {
            text:
              error?.response?.data?.message || this.$t("agenda.changeFailed"),
            type: 1,
          });
        });
    },
    // A alteração feita pelo professor já valeu quando a API respondeu — nada
    // fica pendente de confirmação de aluno. Cada ação diz o que de fato
    // aconteceu; reaproveitar "solicitação enviada" para todas prometia uma
    // espera que não existe.
    successMessage(action, result) {
      if (action === "cancel" && result?.shiftedLessons) {
        return this.$t("agenda.canceledShifted");
      }

      switch (action) {
        case "permanent":
          return this.$t("agenda.permanentDone");
        case "other":
          return this.$t("agenda.rescheduled");
        case "cancel":
          return this.$t("agenda.canceled");
        case "next":
          return this.$t("agenda.subjectChanged");
        default:
          return this.$t("agenda.changeDone");
      }
    },
  },
};
</script>

<style scoped>
.agenda {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-grape, #a826e1);
  padding: 8px 4px 40px;
  color: var(--ink);
}

.agenda__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin: 12px 0 22px;
  flex-wrap: wrap;
}
.agenda__eyebrow {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
}
.agenda__title {
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  font-size: clamp(1.9rem, 4vw, 2.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.agenda__sub {
  margin: 8px 0 0;
  max-width: 48ch;
  font-size: 0.9rem;
  color: var(--muted);
  line-height: 1.5;
}
.agenda__add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 13px;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #a826e1),
    var(--dk-grape-deep, #771ba0)
  );
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 16px 30px -16px rgba(168, 38, 225, 0.8);
  transition: filter 0.15s ease;
}
.agenda__add:hover {
  filter: brightness(1.06);
}
.agenda__add:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
</style>
